// React Router
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';

// Components
import AuthenticatedRouter from './AuthenticatedRouter';
import { DetailViewProvider } from './components/system/detailViews/DetailViewProvider';
import MainLayout from './components/layouts/MainLayout/MainLayout';
import RegistrationGuard from './components/organisms/RegistrationGuard/RegistrationGuard';
import ErrorBoundary from './components/ErrorBoundary';
import PageFallback from './components/atoms/PageFallback/PageFallback';
import AppIntro from './components/molecules/AppIntro/AppIntro';

// State Management
// import { SHOULD_LOAD_DEV_TOOLS } from './constants/environment';
// import { Provider } from 'jotai';
// import store from './store/store';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './graphql/queryClient';

// React
import { lazy, Suspense } from 'react';

// Toast Notifications
import { ToastContainer } from 'react-fox-toast';

// Feature flags
import { isFeatureEnabled, FEATURES } from './utils/featureFlags';

// Constants
import { INQUIRY_SEGMENTS_DEFAULT } from './constants/inquirySegments';

// Lazy Loaded Components
const Jobs = lazy(() => import('./components/pages/Jobs/Jobs'));
const Clients = lazy(() => import('./components/pages/Clients/Clients'));
const Vendors = lazy(() => import('./components/pages/Vendors/Vendors'));
const Agencies = lazy(() => import('./components/pages/Agencies/Agencies'));
const Inspectors = lazy(
  () => import('./components/pages/Inspectors/Inspectors')
);
const InspectorsSkillsManagement = lazy(
  () => import('./components/pages/Inspectors/InspectorsSkillsManagement')
);
const InspectorsCertificatesManagement = lazy(
  () => import('./components/pages/Inspectors/InspectorsCertificatesManagement')
);
const ManageCities = lazy(
  () => import('./components/pages/DataManagement/ManageCities')
);
const ManageExchangeRates = lazy(
  () => import('./components/pages/DataManagement/ManageExchangeRates')
);
const ManageStandardsMaster = lazy(
  () => import('./components/pages/DataManagement/ManageStandardsMaster')
);
const JobGroups = lazy(() => import('./components/pages/JobGroups/JobGroups'));
const Inquiries = lazy(() => import('./components/pages/Inquiries/Inquiries'));
const QuotationDeepLinkPage = lazy(
  () => import('./components/pages/Inquiries/QuotationDeepLinkPage')
);
const InquiryDeepLinkPage = lazy(
  () => import('./components/pages/Inquiries/InquiryDeepLinkPage')
);
const UserManagement = lazy(
  () => import('./components/pages/UserManagement/UserManagement')
);
const ClientQmsCertificates = lazy(
  () => import('./components/pages/ClientQmsCertificates/ClientQmsCertificates')
);
const FrameworkAgreements = lazy(
  () => import('./components/pages/FrameworkAgreements/FrameworkAgreements')
);
const FrameworkAgreementDeepLinkPage = lazy(
  () =>
    import(
      './components/pages/FrameworkAgreements/FrameworkAgreementDeepLinkPage'
    )
);

/** Preserves old bookmarked URLs under `/client-qms-certificates/…`. */
const LegacyClientQmsCertificatesRedirect = () => {
  const { tab } = useParams();
  return <Navigate to={`/client-certificates/${tab ?? 'all'}`} replace />;
};

/**
 * Redirects `/inquiries` to the default segment tab while preserving the query
 * string, so old bookmarks (`/inquiries?filters=…`) and contextual deep links
 * keep their params instead of dropping them on the redirect.
 */
const InquiriesIndexRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/inquiries/${INQUIRY_SEGMENTS_DEFAULT}${location.search}`}
      replace
    />
  );
};

/**
 * App - The main application component that sets up routing, state management,
 * and other global configurations for the application.
 *
 * @returns {JSX.Element} The rendered App component.
 */
function App() {
  // Jotai DevTools - commented out as jotai atoms are no longer in use
  // const [DevTools, setDevTools] = useState(null);
  // useEffect(() => {
  //   if (SHOULD_LOAD_DEV_TOOLS) {
  //     Promise.all([
  //       import('jotai-devtools'),
  //       import('jotai-devtools/styles.css'),
  //     ])
  //       .then(([{ DevTools }]) => {
  //         setDevTools(() => DevTools);
  //       })
  //       .catch((error) => {
  //         console.warn('Failed to load jotai-devtools:', error);
  //       });
  //   }
  // }, []);

  /**
   * return - Returns the main application component.
   *
   * @returns {JSX.Element} The rendered App component.
   */
  return (
    // Error Boundary to catch and handle errors in the application
    <ErrorBoundary>
      {/* Jotai Provider - commented out as jotai atoms are no longer in use */}
      {/* <Provider store={store}> */}
      {/* {SHOULD_LOAD_DEV_TOOLS && DevTools && <DevTools store={store} />} */}
      {/* QueryClientProvider for React Query */}
      <QueryClientProvider client={queryClient}>
        {/* ToastContainer for toast notifications */}
        <ToastContainer
          toastTypeTheming={{
            promise: {
              style: {
                background: '#fefce8',
                borderColor: '#b45309',
              },
            },
            custom: {
              style: {
                background: '#fefce8',
                borderColor: '#b45309',
              },
            },
            success: {
              className:
                'bg-green-100 border-green-800 text-green-800 rounded-lg shadow-md p-4',
            },
            error: {
              className:
                'bg-red-100 border-red-800 text-red-800 rounded-lg shadow-md p-4',
            },
            warning: {
              className:
                'bg-yellow-100 border-yellow-800 text-yellow-800 rounded-lg shadow-md p-4',
            },
          }}
        />

        <Router>
          {/*
            DetailViewProvider hosts the app's shared command/query detail
            modals. It sits inside Router (views may use router hooks) and
            QueryClientProvider, and is inert until a page calls
            useDetailViews().open(...).
          */}
          <DetailViewProvider>
            {/* AuthenticatedRouter for authenticated routes */}
            <AuthenticatedRouter>
              {/* {isLocalEnv && <ReactQueryDevtools />} */}
              <div className="min-h-screen w-screen">
                {/* Suspense for lazy loaded components */}
                <Suspense fallback={<PageFallback />}>
                  {/* Routes for the application */}
                  <Routes>
                    {/* Home route */}
                    <Route
                      path="/"
                      element={
                        <MainLayout key="home" pageName="Home">
                          <AppIntro />
                        </MainLayout>
                      }
                    />
                    {/* Data Management - Cities route */}
                    {isFeatureEnabled(FEATURES.DATA_MANAGEMENT) && (
                      <Route
                        path="/data-management/cities"
                        element={<ManageCities />}
                      />
                    )}
                    {/* Data Management - Exchange Rates route */}
                    {isFeatureEnabled(FEATURES.DATA_MANAGEMENT) && (
                      <Route
                        path="/data-management/exchange-rates"
                        element={<ManageExchangeRates />}
                      />
                    )}
                    {isFeatureEnabled(FEATURES.STANDARDS_MASTER) && (
                      <Route
                        path="/data-management/standards-master"
                        element={<ManageStandardsMaster />}
                      />
                    )}
                    {/* Jobs routes — /jobs redirects to default segment tab */}
                    {isFeatureEnabled(FEATURES.JOBS) && (
                      <>
                        <Route
                          path="/jobs"
                          element={
                            <RegistrationGuard moduleName="Jobs">
                              <Navigate to="/jobs/current" replace />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/jobs/:segment"
                          element={
                            <RegistrationGuard moduleName="Jobs">
                              <Jobs />
                            </RegistrationGuard>
                          }
                        />
                      </>
                    )}
                    {/* Job Groups route */}
                    {isFeatureEnabled(FEATURES.WORK_ORDERS) && (
                      <Route
                        path="/work-orders"
                        element={
                          <RegistrationGuard moduleName="Jobs">
                            <JobGroups />
                          </RegistrationGuard>
                        }
                      />
                    )}
                    {/* Clients route */}
                    {isFeatureEnabled(FEATURES.CLIENTS) && (
                      <Route
                        path="/clients"
                        element={
                          <RegistrationGuard moduleName="Clients">
                            <Clients />
                          </RegistrationGuard>
                        }
                      />
                    )}
                    {/* Vendors route */}
                    {isFeatureEnabled(FEATURES.VENDORS) && (
                      <Route path="/vendors" element={<Vendors />} />
                    )}
                    {/* Inspectors route */}
                    {isFeatureEnabled(FEATURES.INSPECTORS) && (
                      <Route path="/inspectors" element={<Inspectors />} />
                    )}
                    {isFeatureEnabled(FEATURES.INSPECTORS) && (
                      <Route
                        path="/inspectors/skills-management"
                        element={<InspectorsSkillsManagement />}
                      />
                    )}
                    {isFeatureEnabled(FEATURES.INSPECTORS) && (
                      <Route
                        path="/inspectors/certificates-management"
                        element={<InspectorsCertificatesManagement />}
                      />
                    )}
                    {/* Agencies route */}
                    {isFeatureEnabled(FEATURES.AGENCIES) && (
                      <Route path="/agencies" element={<Agencies />} />
                    )}
                    {isFeatureEnabled(FEATURES.CLIENT_QMS_CERTIFICATES) && (
                      <>
                        <Route
                          path="/client-certificates"
                          element={
                            <RegistrationGuard moduleName="Clients">
                              <Navigate to="/client-certificates/all" replace />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/client-certificates/:tab"
                          element={
                            <RegistrationGuard moduleName="Clients">
                              <ClientQmsCertificates />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/client-qms-certificates/:tab"
                          element={
                            <RegistrationGuard moduleName="Clients">
                              <LegacyClientQmsCertificatesRedirect />
                            </RegistrationGuard>
                          }
                        />
                      </>
                    )}
                    {/* Inquiries routes — /inquiries redirects to default segment tab */}
                    {isFeatureEnabled(FEATURES.INQUIRIES) && (
                      <>
                        <Route
                          path="/inquiries"
                          element={
                            <RegistrationGuard moduleName="Inquiries">
                              <InquiriesIndexRedirect />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/inquiries/:segment"
                          element={
                            <RegistrationGuard moduleName="Inquiries">
                              <Inquiries />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/inquiry/:inquiryNumber/quotation/:quotationNumber"
                          element={
                            <RegistrationGuard moduleName="Inquiries">
                              <QuotationDeepLinkPage />
                            </RegistrationGuard>
                          }
                        />
                        <Route
                          path="/inquiry/:inquiryNumber"
                          element={
                            <RegistrationGuard moduleName="Inquiries">
                              <InquiryDeepLinkPage />
                            </RegistrationGuard>
                          }
                        />
                      </>
                    )}

                    {isFeatureEnabled(FEATURES.FRAMEWORK_AGREEMENTS) && (
                      <Route
                        path="/framework-agreements"
                        element={
                          <RegistrationGuard moduleName="Clients">
                            <FrameworkAgreements />
                          </RegistrationGuard>
                        }
                      />
                    )}

                    {/* Personalised segment tab in the path (kebab-case, e.g.
                        /framework-agreements/awaiting-self-approval). Same page; the tab is
                        derived from the slug and defaults to ALL. */}
                    {isFeatureEnabled(FEATURES.FRAMEWORK_AGREEMENTS) && (
                      <Route
                        path="/framework-agreements/:segment"
                        element={
                          <RegistrationGuard moduleName="Clients">
                            <FrameworkAgreements />
                          </RegistrationGuard>
                        }
                      />
                    )}

                    {isFeatureEnabled(FEATURES.FRAMEWORK_AGREEMENTS) && (
                      <Route
                        path="/framework-agreement/:fwAgreementNo"
                        element={
                          <RegistrationGuard moduleName="Clients">
                            <FrameworkAgreementDeepLinkPage />
                          </RegistrationGuard>
                        }
                      />
                    )}

                    {/* Hidden User Management route - accessible only via direct URL */}
                    {isFeatureEnabled(FEATURES.USER_MANAGEMENT) && (
                      <Route
                        path="/data-management/user-management"
                        element={
                          <RegistrationGuard moduleName="User Management">
                            <UserManagement />
                          </RegistrationGuard>
                        }
                      />
                    )}

                    {/* Handle callback route */}
                    <Route
                      path="/callback"
                      element={<Navigate to="/" replace />}
                    />

                    {/* Unknown paths — same intro hub with not-found messaging */}
                    <Route
                      path="*"
                      element={
                        <MainLayout key="not-found" pageName="Home">
                          <AppIntro variant="notFound" />
                        </MainLayout>
                      }
                    />
                  </Routes>
                </Suspense>
              </div>
            </AuthenticatedRouter>
          </DetailViewProvider>
        </Router>
      </QueryClientProvider>
      {/* </Provider> */}
    </ErrorBoundary>
  );
}

App.displayName = 'App';

export default App;
